import React from 'react';

import { Close as CloseIcon } from '@mui/icons-material';
import {
  Box as MuiBox,
  Button,
  Dialog,
  DialogActions as MuiDialogActions,
  DialogContent,
  IconButton,
  Stack,
  styled,
  Typography
} from '@mui/material';
import { useDialogControl } from 'modules';
import { LoadingOverlay } from 'modules/layer0';

import * as dataService from './dataService';



const TitleBox = styled(MuiBox)({
  width: 'fit-content',
  height: 50,
  display: 'flex',
  alignItems: 'center',
  paddingLeft: 24,
  paddingRight: 48,
  marginTop: 14,
  marginBottom: 14,
  background: 'linear-gradient(91.89deg, rgba(82, 136, 152, 0.6) -128.67%, #528898 112.89%)',
  clipPath: 'polygon(calc(100% - 14px) 0%, 100% 50%, calc(100% - 14px) 100%, 0% 100%, 0 50%, 0% 0%);'
});

const TitleText = styled(Typography)({
  fontWeight: 600,
  color: '#FFFFFF',
  lineHeight: 2
});

const IllustrativeWordingStack = styled(Stack)({
  backgroundColor: '#486C82',
  borderRadius: '10px',
  color: '#FFFFFF',
  padding: '20px'
});

const DialogActions = styled(MuiDialogActions)(({ theme }) => ({
  padding: theme.spacing(2)
}));

const Box = styled(MuiBox)({
  display: 'flex',
  justifyContent: 'space-between'
});



export interface IProps {
  title: string;
}

export const FC: React.FC<IProps> = props => {
  const control = useDialogControl({});
  const { fnRunQuery, loading, data } = dataService.useLegalTermDetail();

  return (
    <>
      <LoadingOverlay active={loading} />

      <Button
        variant="contained"
        color="primary"
        style={{ width: 'fit-content' }}
        disabled={loading}
        onClick={async () => {
          await fnRunQuery(props.title);
          control.openDialog();
        }}>
        READ MORE
      </Button>

      <Dialog
        open={control.isOpen}
        onClose={control.closeDialog}
        maxWidth='lg'>
        {!loading && (
          <>
            <Box>
              <TitleBox>
                <TitleText variant="h2">{data?.title}</TitleText>
              </TitleBox>

              <DialogActions>
                <IconButton
                  disabled={loading}
                  onClick={control.closeDialog}>
                  <CloseIcon />
                </IconButton>
              </DialogActions>
            </Box>

            <DialogContent>
              <Stack spacing={4}>
                {__renderSection('Description', data?.description)}
                {__renderSection('Semantics', data?.semantics, __getIllustrativeWordingElement(data?.illustrativeWording))}
                {__renderSection('What\'s typical', data?.whatsTypical)}
                {__renderSection('Where else to check in term sheet', data?.whereElseToCheckInTermSheet)}
                {__renderSection('Scenario Modeling', data?.scenarioModeling)}
                {__renderSection('Where to find in full docs', data?.whereToFindInFullDocs)}
              </Stack>
            </DialogContent>
          </>
        )}
      </Dialog>
    </>
  );
};



function __renderSection(title: string, content?: string, child?: React.ReactNode) {
  return (
    <Stack spacing={2}>
      <Typography variant="h3">{title}</Typography>
      <Typography fontSize={18}>{content ?? 'None'}</Typography>
      {child}
    </Stack>
  );
}



function __getIllustrativeWordingElement(content?: string) {
  return content ? (
    <IllustrativeWordingStack spacing={1}>
      <Typography variant="h3" fontSize={16} color="white">Illustrative Wording:</Typography>
      <Typography fontStyle="italic" color="white">{content}</Typography>
    </IllustrativeWordingStack>
  ) : null;
}
